import DownloadIcon from '@mui/icons-material/Download';
import TableViewIcon from '@mui/icons-material/TableView';
import { Feature } from 'geojson';
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { downloadDataAsCSV } from "../helpers/common";
import styles from "../styles/components/area-summary-table.module.scss";
import tableStyles from "../styles/components/table.module.scss";

import { Warning } from '@mui/icons-material';
import { Button, IconButton, Tab, Tabs, Tooltip, tabsClasses } from '@mui/material';
import classNames from 'classnames';
import numeral from 'numeral';
import { getShapeInfo } from '../api/locations';
import { LocationsContext } from '../context/locations';
import { MapDataContext } from '../context/map-data';
import { MapLocation } from '../core/global';
import { getMinMaxValuesFromGeomShape } from '../helpers/core';
import { ColorSwatch } from './color-picker';
import { InfoMessage, WarningMessage } from './error-message';
import { InfoModal } from './info-modal';
import Loader from './loader';
import { StyledMarkdown } from './typeface';

interface TableProps {
  minValues: Feature[]
  maxValues: Feature[]
  currentPropertyKey: string
  units: string
}

const getFormattedValue = (val: any, currentPropertyKey: string, units: string, raw: boolean = false) => {
  if (val && val.properties && Object.prototype.hasOwnProperty.call(val.properties, currentPropertyKey)) {
    const value = val.properties[currentPropertyKey]
    
    if (raw) return value

    if (typeof value !== 'undefined') {
      return `${numeral(value).format("0,0.00")} ${units}`
    }
  }
  return ''
}

export const Table = ({minValues, maxValues, currentPropertyKey, units}: TableProps) => {
  const {
    setExtremeFeatureMaxValue,
    setExtremeFeatureMinValue,
    updateMeshData,
    setMapCenter,
  } = useContext(MapDataContext);

  if (minValues.length === 0 && maxValues.length === 0) return null

  const data = minValues.length > 0 ? [...minValues] : [...maxValues]
  return (
    <table onMouseOut={() => {
      setExtremeFeatureMaxValue(null)
      setExtremeFeatureMinValue(null)
    }}>
      <thead>
        <tr>
          <th>#</th>
          {minValues.length > 0 && <td>min</td>}
          {maxValues.length > 0 && <td>max</td>}
        </tr>
      </thead>
      <tbody>
        {data.map((item, index) => {
          const mn = getFormattedValue(minValues[index], currentPropertyKey, units)
          const mx = getFormattedValue(maxValues[index], currentPropertyKey, units)

          return (
            <tr 
              key={index} 
              className={styles.row}
              onClick={() => {
                const mV = minValues[index]
                const mX = maxValues[index]
                const useItem = mV ? mV : mX
                if (useItem) {
                  const {properties} = useItem
                  if (properties && Object.prototype.hasOwnProperty.call(properties, 'centroid') && properties.centroid) {
                    const {centroid} = properties
                    const [lat, lon] = centroid.split(' ')
                    updateMeshData({
                      coords: `${lat}, ${lon}`
                    })
                    setMapCenter({location: {lat, lng: lon}})
                  }
                }
                
              }}
              onMouseOver={() => {
                const mV = minValues[index]
                const mX = maxValues[index]
                if (mX && Object.prototype.hasOwnProperty.call(mX, 'properties')) {
                  // @ts-ignore
                  setExtremeFeatureMaxValue(mX?.properties.id || null)
                }
                if (mV && Object.prototype.hasOwnProperty.call(mV, 'properties')) {
                  // @ts-ignore
                  setExtremeFeatureMinValue(mV?.properties.id || null)
                }

              }}>
              <th>#{index+1}</th>
              {minValues.length > 0 && <td>{mn}</td>}
              {maxValues.length > 0 && <td>{mx}</td>}
            </tr>
          )  
        })
        }
      </tbody>
    </table>
  )
}

interface FeatureTableProps {
  data: {
    [key: string]: any
  }
}

export const FeatureTable = ({data}: FeatureTableProps) => {

  const headers = useMemo(() => {
    const {features} = data || {}
    if (features && Array.isArray(features) && features.length > 0) {
      const [first] = features
      return Object.keys(first?.properties || {})
    }
    return Object.keys(data || {})
  }, [data])

  if (headers.length === 0) {
    return <WarningMessage message={'There are no feature values to display for this shape.'} icon={<Warning />} />
  }
  
  return (
    <table className={classNames(tableStyles.table, tableStyles.compact)}>
      <thead>
        <tr>
          <th>Key</th>
          <th>Value</th>
        </tr>
      </thead>
      <tbody>
        {(Array.isArray(data) ? data : [data] ).map((feature: any, index: number) => {
          return (
            <React.Fragment key={index}>
              {headers.map((key: string, index: number) => {
                const value = feature[key]
                let useValue: string | React.ReactElement<any> = typeof value === 'number' ? numeral(value).format("0,0.00") : `${value}`
                let longText = false
                if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, '@type') && value['@type'] === 'html' && value.value) {
                  useValue = <StyledMarkdown>{value.value}</StyledMarkdown>
                  longText = true
                }

                // HACK: color is a reserved word in our system, so we need to remove it from render
                if (key === 'color') {
                  return null
                }

                return (
                  <tr
                    key={index} 
                    className={styles.row}
                  >
                    <th className={tableStyles.fieldName}>{key}</th>
                    <td className={classNames(tableStyles.fieldValue, {
                      [tableStyles.longText]: longText
                    })}>{useValue}</td>
                  </tr>
                )
              })}
            </React.Fragment>
          );  
        })
        }
      </tbody>
    </table>
  );
}

interface TabContentProps {
  tabIndex: number
}

export const TabContent = ({
  tabIndex
}: TabContentProps) => {
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [tabContentData, setTabContentData] = useState<{geoms: any[], summary: any[], shape: any[]} | null>(null);
  const {
    showMin,
    showMax,
    currentLayer,
    dataSourceModel,
    dataSources,
    setMapCenter
  } = useContext(MapDataContext);

  const {
    visibleLayerLocationShapes,
    setCurrentShapeUid,
    setVisibleLocationFullSummary,
  } = useContext(LocationsContext);

  useEffect(() => {
    const execute = async () => {
      const shape_uid = visibleLayerLocationShapes && visibleLayerLocationShapes[tabIndex]?.shape_uid || null
      if (shape_uid) {
        const result = await getShapeInfo({shape_uid})
        const {error, message, response} = result
        const useContent = response || null
        if (error !== 200) {
          // 
        } else {
          // 
        }
        setCurrentShapeUid(shape_uid)
        setTabContentData(useContent)
        setVisibleLocationFullSummary(useContent)
      }
      setLoading(false)
    }
    setLoading(true)
    execute()
  }, [tabIndex])

  const hasFeatures = useMemo(() => {
    const currentFeature: any = visibleLayerLocationShapes && visibleLayerLocationShapes[tabIndex]
    return currentFeature?.features
  }, [visibleLayerLocationShapes])

  const {extremeFeatureMinForShape, extremeFeatureMaxForShape, hasValues} = useMemo(() => {
    const {geoms} = tabContentData || {geoms: []}
    let extremeFeatureMinForShape: Feature[] = []
    let extremeFeatureMaxForShape: Feature[] = []
    let hasValues = false
    if (currentLayer && dataSourceModel && geoms.length > 0) {
      hasValues = true
      if (showMin) {
        extremeFeatureMinForShape = getMinMaxValuesFromGeomShape({currentLayer, dataSourceModel, data: geoms, type: 'min'})
      } else if (showMax) {
        extremeFeatureMaxForShape  = getMinMaxValuesFromGeomShape({currentLayer, dataSourceModel, data: geoms, type: 'max'})
      }  
    }

    return {extremeFeatureMinForShape, extremeFeatureMaxForShape, hasValues}
  }, [currentLayer, tabContentData, dataSourceModel, showMin, showMax])

  if (!visibleLayerLocationShapes || !(visibleLayerLocationShapes && visibleLayerLocationShapes[tabIndex])) {
    return null
  }


  return (
  <>
    <div className={styles.tableWrapper}>
      <div>
        <div className={styles.buttonRow}>
          {currentLayer ?
            <h4>{currentLayer?.title}</h4>
          :
            <InfoMessage message={'No current layer is selected, please select a layer to see min/max values for the given shape.'} />
          }
          
          <div>
          <Tooltip
            title={!hasFeatures ? 'No shape values' : 'View shape values'}
            placement="right"
            arrow
          >
            <span>
              <IconButton
                disabled={!hasFeatures}
                size='small'
                onClick={() => {
                  setShowInfoModal(true)
                }
              }>
                <TableViewIcon />
              </IconButton>
            </span>
          </Tooltip>
          </div>
        </div>
      </div>
      {
        loading ?
        <Loader small center />
        :
        <div>
          {!hasValues ?
            <InfoMessage message={`We don't have any data for this area.`} />
          :
            <Table 
              minValues={extremeFeatureMinForShape} 
              maxValues={extremeFeatureMaxForShape} 
              currentPropertyKey={currentLayer?.valueKey || ''} 
              units={currentLayer?.units || ''} 
            />
          }
        </div>
      }
    </div>
    {showInfoModal && 
      <InfoModal
        maxWidth="lg"
        open={true}
        title={'Explore shape values'}
        content={<>
          <StyledMarkdown>

{
  visibleLayerLocationShapes[tabIndex].source !== 'shape-file' ?
`These are values that have been read from your **.json** file *properties* column.`
:
`These are values that have been read from your **.dbf** file associated with your shape.`}
          </StyledMarkdown>
          <Button
            title={'shape-values'}
            startIcon={<DownloadIcon />}
            sx={{
              mt: 0,
              mb: "16px"
            }}
            onClick={() => {
              const useData: {[key: string]: string | number | null} = visibleLayerLocationShapes[tabIndex].features.properties || null
              // HACK: color is a reserved word in our system, so we need to remove it from the download
              if (useData && Object.prototype.hasOwnProperty.call(useData, 'color')) {
                delete useData.color
              }
              downloadDataAsCSV([useData], `shape values - ${visibleLayerLocationShapes[tabIndex].title} - ${visibleLayerLocationShapes[tabIndex].shape_title}`)
            } }
          >
            Download CSV
          </Button>
          <FeatureTable 
            data={visibleLayerLocationShapes[tabIndex].features?.properties || {}}
          />
        </>}
        onClose={() => {
          setShowInfoModal(false)
        }}
      />
    }
  </>)
}

interface TabPanelProps {
  children: React.ReactElement<any>
  value: number
  index: number
  style?: React.CSSProperties
}
function TabPanel({ children, value, index, ...other }: TabPanelProps) {

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <>
          {children}
        </>
      )}
    </div>
  );
}

export const AreaSummaryTable = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [showInfoModalIndex, setShowInfoModalIndex] = useState<null | number>(null);
  const [showPicker, setShowPicker] = useState<boolean>(false);

  const {
    showMin,
    showMax,
    currentLayer,
    dataSourceModel,
    dataSources,
    setMapCenter
  } = useContext(MapDataContext);

  const {
    visibleLayerLocationShapes,
    setCurrentShapeUid,
  } = useContext(LocationsContext);

  const handleTabChange = useCallback((event: React.SyntheticEvent, newTabIndex: number) => {
    if (visibleLayerLocationShapes) {
      setCurrentShapeUid(visibleLayerLocationShapes[newTabIndex].shape_uid || null)
      setTabIndex(newTabIndex);
      let centerPoint: MapLocation | null = null
      const centroid = visibleLayerLocationShapes[newTabIndex].centroid || null
      if (centroid && typeof centroid === 'string') {
        const [lat, lng] = centroid.split(' ')
        centerPoint = {location: {lat, lng}}
      }
      if (centerPoint) {
        setMapCenter(centerPoint)
      }
    }
  }, [visibleLayerLocationShapes])

  useEffect(() => {
    setTabIndex(0)
  }, [])

  if (dataSourceModel === null || dataSources === null || !visibleLayerLocationShapes) {
    return null
  }

  return (<>
    <Tabs
      className={styles.tabs}
      value={tabIndex}
      onChange={handleTabChange}
      variant="scrollable"
      scrollButtons
      allowScrollButtonsMobile
      sx={{
        [`& .${tabsClasses.scrollButtons}`]: {
          '&.Mui-disabled': { opacity: 0.3 },
        },
      }}
    >
      {
        visibleLayerLocationShapes.map( (row, index) => {
          const {shape_title, features, color, hidden} = row
          const useTitle = shape_title || 'Unnamed'
          // if (features  && features.properties) {
          //   const {properties} = features
          //   useTitle = properties.name || properties.NAME || useTitle
          // }
          return <Tab 
            key={index} 
            label={
              <Tooltip
                title={`${useTitle}`}
                placement="top"
                arrow
              >
                <span>
                  <div className={styles.tabRow}>
                    <ColorSwatch color={color || '#ffffff'} hidden={hidden} />
                    {useTitle.length > 16 ? `${useTitle.substring(0, 12)}...` : useTitle}
                  </div>
                </span>
              </Tooltip>
            } />
        })
      }
    </Tabs>
    <TabContent tabIndex={tabIndex} />
  </>)
}



